<script lang="ts" setup>
import { getFullscreenLink } from "~/utilities/urls"

const productVariants = useProductVariantsStore()
const productContentStore = useProductContentStore()
const audioStore = useAudioStore()

const { currentTime, currentDuration, currentTrack, tts, isFullscreen, activePlayer } = storeToRefs(audioStore)

const playerSizeIcon = computed(
  () => isFullscreen.value ? "minimize" : "expand-diagonal",
)

const isMinimizedOutsideOfArticle = computed(
  () => !productContentStore.activeArticle && !isFullscreen.value,
)

const hasPaybanner = computed(
  () => !productVariants.activeVariant?.userHasLicence,
)

const titleAttrs = computed(() => ({
  text: tts.value.status === "loading" ? "Laster lyd..." : currentTrack.value?.title,
  class: [
    "w-full text-center text-label-lg font-extrabold",
    {
      "max-w-32 truncate !pt-0 text-xl sm:max-w-48 md:max-w-md lg:max-w-[15rem] xl:max-w-lg": !isFullscreen.value,
      "animate-color-pulse": tts.value.status === "loading",
    },
  ],
}))

const subjectTitle = computed(() => `${productContentStore.activeSubject?.priority}. ${productContentStore.activeSubject?.title}`)
</script>

<template>
  <aside
    v-show="activePlayer && productVariants.activeVariant"
    id="fullscreen-element"
    aria-label="Lydavspiller"
    tabindex="0"
    class="fixed bottom-28 left-1/2 z-30 flex h-fit w-[90%] -translate-x-1/2 flex-col items-center justify-center transition-opacity focus:border-[--theme-light]"
    :class="{
      '!bottom-0 !h-full w-full': isFullscreen,
      '!bottom-32': hasPaybanner && isMinimizedOutsideOfArticle,
      '!bottom-10': !hasPaybanner && isMinimizedOutsideOfArticle,
    }"
    @keyup.esc="isFullscreen = false"
    @keyup.exact.down="isFullscreen = false"
    @keyup.exact.left="currentTime -= 15"
    @keyup.exact.right="currentTime += 15"
    @keyup.exact.space="audioStore.togglePlayState()"
  >
    <div
      class="relative flex w-full items-center justify-start gap-6 rounded-lg bg-darker shadow-lg md:gap-10"
      :class="{
        'size-full flex-col justify-end rounded-none px-0 pb-7 md:p-10': isFullscreen,
        'px-10 py-4': !isFullscreen,
      }"
    >
      <UAudioPlayerPlaylist v-if="isFullscreen" />
      <div class="max-w-[90%] flex-col items-center justify-center md:max-w-[70%]">
        <div
          class="flex w-full flex-col items-center justify-center gap-2 font-national2 text-platform-paper md:gap-2"
        >
          <NuxtLink
            v-if="isFullscreen && productContentStore.activeSubject?.frontendHref"
            :to="getFullscreenLink(productContentStore.activeSubject.frontendHref, false)"
            class="text-normal text-label-sm text-center  text-[--theme-lighter] hover:text-[--theme-lighter] md:text-platform-paper"
          >
            {{ subjectTitle }}
          </NuxtLink>
          <h5
            v-else-if="isFullscreen && !productContentStore.activeSubject?.frontendHref"
            class="text-normal text-label-sm text-center text-platform-paper"
          >
            {{ subjectTitle }}
          </h5>
          <h4
            class="hidden lg:block"
            :class="titleAttrs.class"
            v-text="titleAttrs.text"
          />
          <button
            class="lg:hidden"
            :class="titleAttrs.class"
            aria-controls="fullscreen-element"
            @click="isFullscreen = true"
            v-text="titleAttrs.text"
          />
        </div>
        <h5 v-if="!isFullscreen" class="block text-lighter lg:hidden">
          -{{ getMinutesAndSeconds(currentDuration - currentTime) }}
        </h5>
      </div>
      <div
        class="flex w-full items-center justify-between gap-2 md:gap-4 "
        :class="{
          'flex-col px-8 md:max-w-[70%] md:px-0 md:pb-10': isFullscreen,
          '!justify-end': !isFullscreen,
        }"
      >
        <UAudioPlayerControls />
        <UAudioSlider :class="{ 'hidden lg:flex': !isFullscreen }" />
        <div class="flex items-center justify-center gap-3">
          <UButton
            aria-controls="fullscreen-element"
            :aria-label="
              isFullscreen
                ? 'Skru av fullskjerm for lydavspilleren'
                : 'Skru på fullskjerm for lydavspilleren'
            "
            :aria-expanded="isFullscreen"
            variant="quite"
            theme="white"
            :class="{
              'absolute right-0 top-0 mr-7 pt-7': isFullscreen,
              'hidden lg:flex': !isFullscreen,
            }"
            class="hover:text-lighter"
            @click="isFullscreen = !isFullscreen"
          >
            <UIcon :name="playerSizeIcon" size="lg" />
          </UButton>
          <UButton
            v-if="!isFullscreen"
            variant="quite"
            theme="white"
            class="hover:text-lighter"
            @click="activePlayer = false"
          >
            <UIcon name="close" size="lg" />
          </UButton>
        </div>
        <p v-if="isFullscreen && currentTrack?.textToSpeech" class="text-text-xxs text-center font-numeraWebRegular italic  text-[--theme-lighter]">
          Stemmen som leser denne artikkelen er laget ved hjelp av kunstig intelligens (KI)
        </p>
      </div>
    </div>
  </aside>
</template>
